/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {BaseError} from './facade/errors';
import {isPrimitive, isStrictStringMap} from './facade/lang';
export const /** @type {?} */ MODULE_SUFFIX = '';

const /** @type {?} */ CAMEL_CASE_REGEXP = /([A-Z])/g;
const /** @type {?} */ DASH_CASE_REGEXP = /-+([a-z0-9])/g;
/**
 * @param {?} input
 * @return {?}
 */
export function camelCaseToDashCase(input: string): string {
  return input.replace(CAMEL_CASE_REGEXP, (...m: any[]) => '-' + m[1].toLowerCase());
}
/**
 * @param {?} input
 * @return {?}
 */
export function dashCaseToCamelCase(input: string): string {
  return input.replace(DASH_CASE_REGEXP, (...m: any[]) => m[1].toUpperCase());
}
/**
 * @param {?} input
 * @param {?} defaultValues
 * @return {?}
 */
export function splitAtColon(input: string, defaultValues: string[]): string[] {
  return _splitAt(input, ':', defaultValues);
}
/**
 * @param {?} input
 * @param {?} defaultValues
 * @return {?}
 */
export function splitAtPeriod(input: string, defaultValues: string[]): string[] {
  return _splitAt(input, '.', defaultValues);
}
/**
 * @param {?} input
 * @param {?} character
 * @param {?} defaultValues
 * @return {?}
 */
function _splitAt(input: string, character: string, defaultValues: string[]): string[] {
  const /** @type {?} */ characterIndex = input.indexOf(character);
  if (characterIndex == -1) return defaultValues;
  return [input.slice(0, characterIndex).trim(), input.slice(characterIndex + 1).trim()];
}
/**
 * @param {?} value
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
export function visitValue(value: any, visitor: ValueVisitor, context: any): any {
  if (Array.isArray(value)) {
    return visitor.visitArray( /** @type {?} */((<any[]>value)), context);
  }

  if (isStrictStringMap(value)) {
    return visitor.visitStringMap( /** @type {?} */((<{[key: string]: any}>value)), context);
  }

  if (value == null || isPrimitive(value)) {
    return visitor.visitPrimitive(value, context);
  }

  return visitor.visitOther(value, context);
}

export interface ValueVisitor {
  visitArray(arr: any[], context: any): any;
  visitStringMap(map: {[key: string]: any}, context: any): any;
  visitPrimitive(value: any, context: any): any;
  visitOther(value: any, context: any): any;
}

export class ValueTransformer implements ValueVisitor {
/**
 * @param {?} arr
 * @param {?} context
 * @return {?}
 */
visitArray(arr: any[], context: any): any {
    return arr.map(value => visitValue(value, this, context));
  }
/**
 * @param {?} map
 * @param {?} context
 * @return {?}
 */
visitStringMap(map: {[key: string]: any}, context: any): any {
    const /** @type {?} */ result: {[key: string]: any} = {};
    Object.keys(map).forEach(key => { result[key] = visitValue(map[key], this, context); });
    return result;
  }
/**
 * @param {?} value
 * @param {?} context
 * @return {?}
 */
visitPrimitive(value: any, context: any): any { return value; }
/**
 * @param {?} value
 * @param {?} context
 * @return {?}
 */
visitOther(value: any, context: any): any { return value; }
}

export class SyncAsyncResult<T> {
/**
 * @param {?} syncResult
 * @param {?=} asyncResult
 */
constructor(public syncResult: T,
public asyncResult: Promise<T> = null) {
    if (!asyncResult) {
      this.asyncResult = Promise.resolve(syncResult);
    }
  }
}

function SyncAsyncResult_tsickle_Closure_declarations() {
 /** @type {?} */
SyncAsyncResult.prototype.syncResult;
 /** @type {?} */
SyncAsyncResult.prototype.asyncResult;
}


export class SyntaxError extends BaseError {}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
